import React, { useState } from "react";
import {
  Box,
  Loader,
  MultiSelect,
  Popover,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import styles from "./sales.module.css";
import { RANGE_OPTIONS, RANGE_LABEL, SalesFilters } from "./filters";
import {
  SalesSearchInput,
  MenuToken,
  TextToken,
  ResultCount,
} from "./salesToolbarParts";
import { AppointmentTag } from "../../../types/appointmentTags";
import AppointmentTagChip from "../../appointmentTags/AppointmentTagChip";

interface SalesToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  filters: SalesFilters;
  onFiltersChange: (next: SalesFilters) => void;
  /** Status value that represents "no status filter" (no token shown). */
  defaultStatus: string;
  statusOptions: { value: string; label: string }[];
  count: number;
  total: number;
  /** Noun for the result count, e.g. "transactions" / "purchases". */
  itemLabel: string;
  searchPlaceholder: string;
  /** Show a small spinner beside + Filter while a fetch is in flight. */
  loading?: boolean;
  /** Optional replacement for the default Date pill (e.g. the reports-style
   *  DateRangeFilter with custom ranges). When omitted, the preset Date pill
   *  is rendered. */
  dateControl?: React.ReactNode;
  /** The location's appointment tags. When non-empty, a Tags multiselect
   *  appears in the filter popover (rows match ANY selected tag). */
  tagOptions?: AppointmentTag[];
}

/** "VIP" / "VIP, Referral" / "3 tags" for the applied Tags token. */
export function tagTokenLabel(
  tagIds: string[],
  tagOptions: AppointmentTag[],
): string {
  if (tagIds.length > 2) return `${tagIds.length} tags`;
  return tagIds
    .map((id) => tagOptions.find((t) => t.id === id)?.name ?? "Tag")
    .join(", ");
}

/**
 * Toolbar shared by the Transactions + Payments Sales screens: pill search
 * field, applied filter tokens, the `+ Filter` popover trigger, a spacer, and
 * the result count. Built from the shared primitives in `salesToolbarParts`
 * (the same pieces the Gift Cards toolbar uses). Every popover control applies
 * on change (no Apply button); each non-default filter surfaces as a
 * dismissible token. The Date token is always shown and is not removable while
 * set to the default range.
 */
export default function SalesToolbar({
  query,
  onQueryChange,
  filters,
  onFiltersChange,
  defaultStatus,
  statusOptions,
  count,
  total,
  itemLabel,
  searchPlaceholder,
  loading = false,
  dateControl,
  tagOptions = [],
}: SalesToolbarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const set = (patch: Partial<SalesFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  const statusLabel =
    statusOptions.find((o) => o.value === filters.status)?.label ??
    filters.status;

  return (
    <Box className={styles.toolbar}>
      <SalesSearchInput
        value={query}
        onChange={onQueryChange}
        placeholder={searchPlaceholder}
      />

      {/* Date: a custom control when provided (reports-style range), else the
          preset quick-switch pill. */}
      {dateControl ?? (
        <MenuToken
          label="Date"
          valueLabel={RANGE_LABEL[filters.range]}
          current={filters.range}
          options={RANGE_OPTIONS}
          onPick={(v) => set({ range: v as SalesFilters["range"] })}
        />
      )}

      {/* Status pill only appears once narrowed; clicking it re-opens the same
          options inline (pick the default to clear it). */}
      {filters.status !== defaultStatus && (
        <MenuToken
          label="Status"
          valueLabel={statusLabel}
          current={filters.status}
          options={statusOptions}
          onPick={(v) => set({ status: v })}
        />
      )}

      {/* Text filters: clicking the pill re-opens the filter popover to edit the
          value; the ✕ clears it. */}
      {filters.txn.trim().length > 0 && (
        <TextToken
          label="Transaction #"
          value={filters.txn}
          onEdit={() => setPopoverOpen(true)}
          onRemove={() => set({ txn: "" })}
        />
      )}
      {filters.client.trim().length > 0 && (
        <TextToken
          label="Client"
          value={filters.client}
          onEdit={() => setPopoverOpen(true)}
          onRemove={() => set({ client: "" })}
        />
      )}
      {filters.last4.trim().length > 0 && (
        <TextToken
          label="Card ••"
          value={filters.last4}
          onEdit={() => setPopoverOpen(true)}
          onRemove={() => set({ last4: "" })}
        />
      )}
      {filters.tagIds.length > 0 && (
        <TextToken
          label="Tags"
          value={tagTokenLabel(filters.tagIds, tagOptions)}
          onEdit={() => setPopoverOpen(true)}
          onRemove={() => set({ tagIds: [] })}
        />
      )}

      <Popover
        opened={popoverOpen}
        onChange={setPopoverOpen}
        position="bottom-start"
        width={268}
        radius={12}
        shadow="0 8px 24px rgba(0,0,0,.16)"
        withinPortal
      >
        <Popover.Target>
          <UnstyledButton
            className={`${styles.filterTrigger} ${
              popoverOpen ? styles.filterTriggerActive : ""
            }`}
            onClick={() => setPopoverOpen((o) => !o)}
          >
            <IconPlus size={13} stroke={2.4} />
            Filter
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap={13}>
            <Box>
              <Text className={styles.fieldLabel}>Date range</Text>
              <Select
                data={RANGE_OPTIONS}
                value={filters.range}
                onChange={(v) =>
                  v && set({ range: v as SalesFilters["range"] })
                }
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
              />
            </Box>
            <Box>
              <Text className={styles.fieldLabel}>Status</Text>
              <Select
                data={statusOptions}
                value={filters.status}
                onChange={(v) => v && set({ status: v })}
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
              />
            </Box>
            <Box>
              <Text className={styles.fieldLabel}>Transaction number</Text>
              <TextInput
                placeholder="92093"
                value={filters.txn}
                onChange={(e) => set({ txn: e.currentTarget.value })}
              />
            </Box>
            <Box>
              <Text className={styles.fieldLabel}>Client name</Text>
              <TextInput
                placeholder="Eileen Butler"
                value={filters.client}
                onChange={(e) => set({ client: e.currentTarget.value })}
              />
            </Box>
            <Box>
              <Text className={styles.fieldLabel}>Last 4 digits of card</Text>
              <TextInput
                placeholder="1234"
                inputMode="numeric"
                value={filters.last4}
                onChange={(e) =>
                  set({
                    last4: e.currentTarget.value.replace(/\D/g, "").slice(0, 4),
                  })
                }
              />
            </Box>
            {tagOptions.length > 0 && (
              <Box>
                <Text className={styles.fieldLabel}>Tags</Text>
                <MultiSelect
                  aria-label="Tags"
                  placeholder={
                    filters.tagIds.length === 0 ? "Any tag" : undefined
                  }
                  data={tagOptions.map((t) => ({
                    value: t.id,
                    label: t.emoji ? `${t.emoji} ${t.name}` : t.name,
                  }))}
                  renderOption={({ option }) => {
                    const tag = tagOptions.find((t) => t.id === option.value);
                    return tag ? (
                      <AppointmentTagChip name={tag.name} emoji={tag.emoji} />
                    ) : (
                      option.label
                    );
                  }}
                  value={filters.tagIds}
                  onChange={(tagIds) => set({ tagIds })}
                  searchable
                  clearable
                  comboboxProps={{ withinPortal: false }}
                />
              </Box>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {loading && <Loader size="xs" color="var(--brand-teal)" />}

      <Box style={{ flex: 1 }} />

      <ResultCount count={count} total={total} itemLabel={itemLabel} />
    </Box>
  );
}
